import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxEditorModule, Editor } from 'ngx-editor';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';
import { Subject, from, Subscription, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, filter } from 'rxjs/operators';
import { SafeHtmlPipe } from '../shared/safe-html.pipe';

export interface ContactMessageDto {
  id: number;
  userId: number | null;
  userName: string;
  userUsername?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  recipientUserId?: number | null;
  recipientUserName?: string;
  recipientUsername?: string | null;
  recipientFirstName?: string | null;
  recipientLastName?: string | null;
  parentId?: number | null;
  topic: string;
  messageText: string;
  createdAt: string;
  readAt: string | null;
  receiverFlag: string | null;
  receiverDeleted: number;
  senderDeleted: number;
  hasAttachment?: boolean;
  attachmentFilename?: string | null;
}

@Component({
  selector: 'app-admin-messages',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NgxEditorModule, SafeHtmlPipe],
  templateUrl: './admin-messages.component.html',
  styleUrl: './admin-messages.component.css'
})
export class AdminMessagesComponent implements OnInit, OnDestroy {
  sessionId = '';
  loading = false;
  error = '';
  messages: ContactMessageDto[] = [];
  selectedMessage: ContactMessageDto | null = null;
  threadMessages: ContactMessageDto[] = [];
  selectedLoading = false;
  deleteConfirm: ContactMessageDto | null = null;
  deleteInProgress = false;

  composeModal = false;
  composeMode: 'new' | 'reply' = 'new';
  composeCancelConfirm = false;
  composeSendConfirm = false;
  composeModalTop = 80;
  composeModalLeft = 100;
  private composeDragStart = { x: 0, y: 0, top: 0, left: 0 };
  private composeDragMoveBound?: (e: MouseEvent) => void;
  private composeDragUpBound?: () => void;

  sortColumn = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  inboxView: 'inbox' | 'sent' = 'inbox';
  flagFilter: '' | 'red' | 'yellow' | 'green' = '';
  unreadFilter = false;
  page = 0;
  pageSize = 100;
  totalElements = 0;
  totalPages = 0;

  newMessageRecipientQuery = '';
  newMessageRecipient: { id: number; displayName: string } | null = null;
  newMessageRecipientResults: { id: number; username: string; displayName: string }[] = [];
  newMessageRecipientSearching = false;
  newMessageTopic = '';
  newMessageHtml = '';
  newMessageAttachment: File | null = null;
  newMessageSending = false;
  newMessageSendError = '';
  maxAttachmentSizeMb = 5;
  newMessageEditor: Editor | null = null;
  private recipientSearch$ = new Subject<string>();
  private recipientSearchSub?: Subscription;
  private refreshSub?: Subscription;
  private readonly refreshIntervalMs = 30000;

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    this.loadMessages();
    this.loadContactConfig();
    this.recipientSearchSub = this.recipientSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        this.newMessageRecipientSearching = true;
        return from(this.adminRest.searchAdminUsers(this.sessionId, q));
      })
    ).subscribe({
      next: (users) => {
        this.newMessageRecipientResults = users.map(u => ({ id: u.id, username: u.username, displayName: u.displayName || u.username }));
        this.newMessageRecipientSearching = false;
      },
      error: () => { this.newMessageRecipientSearching = false; }
    });
    this.refreshSub = interval(this.refreshIntervalMs).pipe(
      filter(() => !!this.sessionId && !this.loading && !this.composeModal)
    ).subscribe(() => this.loadMessagesSilent());
  }

  ngOnDestroy() {
    this.recipientSearchSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    if (this.newMessageEditor) {
      this.newMessageEditor.destroy();
      this.newMessageEditor = null;
    }
  }

  async loadContactConfig() {
    const cfg = await this.adminRest.getAdminContactConfig(this.sessionId);
    if (cfg) this.maxAttachmentSizeMb = cfg.maxAttachmentSizeMb ?? 5;
  }

  get sortedMessages(): ContactMessageDto[] {
    const arr = [...this.messages];
    const mult = this.sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (this.sortColumn) {
        case 'userName': va = (this.inboxView === 'sent' ? (a.recipientUsername || '') : (a.userUsername || '')).toLowerCase(); vb = (this.inboxView === 'sent' ? (b.recipientUsername || '') : (b.userUsername || '')).toLowerCase(); break;
        case 'topic': va = (a.topic || '').toLowerCase(); vb = (b.topic || '').toLowerCase(); break;
        case 'createdAt': va = a.createdAt || ''; vb = b.createdAt || ''; break;
        default: va = a.createdAt || ''; vb = b.createdAt || '';
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return arr;
  }


  async loadMessages() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const sort = `${this.sortColumn},${this.sortDir}`;
      const inbox = this.inboxView === 'inbox';
      const flag = inbox ? (this.flagFilter || undefined) : undefined;
      const unread = inbox ? (this.unreadFilter || undefined) : undefined;
      const res = await this.adminRest.getAdminContactMessages(this.sessionId, this.page, this.pageSize, sort, inbox, flag, unread);
      this.messages = res.content || [];
      this.totalElements = res.totalElements ?? 0;
      this.totalPages = res.totalPages ?? 0;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load messages';
    } finally {
      this.loading = false;
    }
  }

  /** Background refresh without loading spinner */
  private async loadMessagesSilent() {
    if (!this.sessionId) return;
    try {
      const sort = `${this.sortColumn},${this.sortDir}`;
      const inbox = this.inboxView === 'inbox';
      const flag = inbox ? (this.flagFilter || undefined) : undefined;
      const unread = inbox ? (this.unreadFilter || undefined) : undefined;
      const res = await this.adminRest.getAdminContactMessages(this.sessionId, this.page, this.pageSize, sort, inbox, flag, unread);
      this.messages = res.content || [];
      this.totalElements = res.totalElements ?? 0;
      this.totalPages = res.totalPages ?? 0;
      if (this.threadMessages.length > 0) {
        const thread = await this.adminRest.getAdminContactMessageThread(this.sessionId, this.threadMessages[0].id);
        if (thread?.length) this.threadMessages = thread;
      }
    } catch {
      // ignore errors on background refresh
    }
  }

  setInboxView(view: 'inbox' | 'sent') {
    this.inboxView = view;
    this.page = 0;
    if (view === 'sent') {
      this.flagFilter = '';
      this.unreadFilter = false;
    }
    this.loadMessages();
  }

  setFlagFilter(flag: '' | 'red' | 'yellow' | 'green') {
    this.flagFilter = flag;
    this.page = 0;
    this.loadMessages();
  }

  setUnreadFilter(value: boolean) {
    this.unreadFilter = value;
    this.page = 0;
    this.loadMessages();
  }

  goToPage(p: number) {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    this.loadMessages();
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'desc';
    }
    this.page = 0;
    this.loadMessages();
  }

  isInThread(msgId: number): boolean {
    return this.threadMessages.some(tm => tm.id === msgId);
  }

  get replyToName(): string {
    const last = this.threadMessages.length ? this.threadMessages[this.threadMessages.length - 1] : null;
    if (!last) return '';
    return last.recipientUserId ? (last.recipientUserName ?? '') : (last.userName ?? '');
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-chevron-expand';
    return this.sortDir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
  }

  async selectMessage(msg: ContactMessageDto) {
    this.selectedMessage = null;
    this.threadMessages = [];
    this.selectedLoading = true;
    try {
      const thread = await this.adminRest.getAdminContactMessageThread(this.sessionId, msg.id);
      this.threadMessages = thread || [];
      this.selectedMessage = this.threadMessages[0] ?? null;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load message';
    } finally {
      this.selectedLoading = false;
    }
  }

  closeDetail() {
    this.selectedMessage = null;
    this.threadMessages = [];
  }

  onComposeDragStart(e: MouseEvent) {
    if ((e.target as HTMLElement).closest('.btn-close')) return;
    this.composeDragStart = { x: e.clientX, y: e.clientY, top: this.composeModalTop, left: this.composeModalLeft };
    this.composeDragMoveBound = (ev: MouseEvent) => {
      this.composeModalTop = this.composeDragStart.top + ev.clientY - this.composeDragStart.y;
      this.composeModalLeft = this.composeDragStart.left + ev.clientX - this.composeDragStart.x;
    };
    this.composeDragUpBound = () => {
      document.removeEventListener('mousemove', this.composeDragMoveBound!);
      document.removeEventListener('mouseup', this.composeDragUpBound!);
    };
    document.addEventListener('mousemove', this.composeDragMoveBound);
    document.addEventListener('mouseup', this.composeDragUpBound);
  }

  async cycleFlag(msg: ContactMessageDto, e: Event) {
    e.stopPropagation();
    if (!this.sessionId) return;
    const order: (string | null)[] = [null, 'red', 'yellow', 'green'];
    const idx = order.indexOf(msg.receiverFlag);
    const next = order[(idx + 1) % order.length];
    try {
      await this.adminRest.patchAdminContactMessage(this.sessionId, msg.id, { receiverFlag: next ?? undefined });
      msg.receiverFlag = next;
      const inThread = this.threadMessages.find(m => m.id === msg.id);
      if (inThread) inThread.receiverFlag = next;
    } catch (err) {
      this.error = 'Failed to update flag';
    }
  }

  openDeleteConfirm(msg: ContactMessageDto, e: Event) {
    e.stopPropagation();
    this.deleteConfirm = msg;
  }

  cancelDelete() {
    this.deleteConfirm = null;
  }

  async confirmDelete() {
    if (!this.deleteConfirm || !this.sessionId) return;
    this.deleteInProgress = true;
    try {
      const ok = await this.adminRest.deleteAdminContactMessage(this.sessionId, this.deleteConfirm.id, this.inboxView === 'inbox');
      if (ok) {
        if (this.threadMessages.some(m => m.id === this.deleteConfirm!.id)) this.closeDetail();
        this.deleteConfirm = null;
        await this.loadMessages();
      } else {
        this.error = 'Failed to delete';
      }
    } catch (err) {
      this.error = 'Failed to delete';
    } finally {
      this.deleteInProgress = false;
    }
  }

  openNewMessage() {
    this.composeMode = 'new';
    this.composeModal = true;
    this.newMessageRecipient = null;
    this.newMessageRecipientQuery = '';
    this.newMessageRecipientResults = [];
    this.newMessageTopic = '';
    this.newMessageHtml = '';
    this.newMessageAttachment = null;
    this.composeSendConfirm = false;
    this.composeCancelConfirm = false;
    this.newMessageEditor = new Editor();
    this.resetComposePosition();
  }

  openReply() {
    if (!this.threadMessages.length) return;
    const last = this.threadMessages[this.threadMessages.length - 1];
    const recipientId = last.recipientUserId ?? last.userId;
    const recipientName = last.recipientUserId ? (last.recipientUserName ?? '') : (last.userName ?? '');
    const topic = last.topic?.startsWith('Re: ') ? last.topic : 'Re: ' + (last.topic || '');
    this.composeMode = 'reply';
    this.composeModal = true;
    this.newMessageRecipient = { id: recipientId!, displayName: recipientName };
    this.newMessageRecipientQuery = recipientName;
    this.newMessageRecipientResults = [];
    this.newMessageTopic = topic;
    this.newMessageHtml = '';
    this.newMessageAttachment = null;
    this.composeSendConfirm = false;
    this.composeCancelConfirm = false;
    this.newMessageEditor = new Editor();
    this.resetComposePosition();
  }

  private resetComposePosition() {
    this.composeModalTop = 80;
    this.composeModalLeft = 100;
  }

  get canSendCompose(): boolean {
    if (this.composeMode === 'reply') {
      return !!this.newMessageRecipient && !!this.newMessageTopic.trim() && this.hasNewMessageContent;
    }
    return !!this.newMessageRecipient && !!this.newMessageTopic.trim() && this.hasNewMessageContent;
  }

  requestCloseCompose() {
    if (this.isNewMessageEmpty) {
      this.closeCompose();
    } else {
      this.composeCancelConfirm = true;
    }
  }

  closeCompose() {
    this.composeModal = false;
    if (this.newMessageEditor) {
      this.newMessageEditor.destroy();
      this.newMessageEditor = null;
    }
  }

  cancelComposeConfirmClose() {
    this.composeCancelConfirm = false;
  }

  confirmCloseCompose() {
    this.composeCancelConfirm = false;
    this.closeCompose();
  }

  showComposeSendConfirm() {
    if (!this.canSendCompose) return;
    this.newMessageSendError = '';
    this.composeSendConfirm = true;
  }

  cancelComposeSend() {
    this.composeSendConfirm = false;
    this.newMessageSendError = '';
  }

  async confirmSendCompose() {
    if (!this.canSendCompose || !this.sessionId) return;
    this.newMessageSending = true;
    this.newMessageSendError = '';
    try {
      if (this.composeMode === 'reply') {
        const replyToId = this.threadMessages[this.threadMessages.length - 1].id;
        const created = await this.adminRest.replyAdminContactMessage(
          this.sessionId,
          replyToId,
          this.newMessageHtml.trim(),
          this.newMessageAttachment ?? undefined
        );
        this.threadMessages.push(created);
        if (this.inboxView === 'sent') this.messages.unshift(created);
      } else {
        const created = await this.adminRest.createAdminContactMessage(
          this.sessionId,
          this.newMessageRecipient!.id,
          this.newMessageTopic.trim(),
          this.newMessageHtml.trim(),
          this.newMessageAttachment ?? undefined
        );
        if (this.inboxView === 'sent') this.messages.unshift(created);
      }
      this.composeSendConfirm = false;
      this.closeCompose();
    } catch (err: any) {
      this.newMessageSendError = this.getSendErrorMessage(err);
    } finally {
      this.newMessageSending = false;
    }
  }

  private getSendErrorMessage(err: any): string {
    if (err?.status === 413) {
      return `The attachment is too large. Maximum size is ${this.maxAttachmentSizeMb} MB. Please choose a smaller file or remove the attachment.`;
    }
    const msg = err?.error?.error || err?.error?.message || err?.message;
    if (msg && !msg.includes('http://') && !msg.includes('Http failure')) {
      return msg;
    }
    return 'Failed to send message. Please try again.';
  }

  async downloadAttachment(msg: ContactMessageDto) {
    if (!this.sessionId || !msg.hasAttachment) return;
    try {
      const blob = await this.adminRest.getAdminContactMessageAttachment(this.sessionId, msg.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = msg.attachmentFilename || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      this.error = e?.message || 'Failed to download attachment';
    }
  }

  /** Tooltip: first_name, last_name only (no username). */
  getNameTooltip(msg: ContactMessageDto): string {
    if (this.inboxView === 'sent') {
      const fn = msg.recipientFirstName ?? '';
      const ln = msg.recipientLastName ?? '';
      return [fn, ln].filter(Boolean).join(' ').trim() || '—';
    }
    const fn = msg.userFirstName ?? '';
    const ln = msg.userLastName ?? '';
    return [fn, ln].filter(Boolean).join(' ').trim() || '—';
  }

  formatDate(s: string | null): string {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return d.toLocaleString();
    } catch {
      return s;
    }
  }


  onRecipientInput() {
    if (this.newMessageRecipient) {
      this.newMessageRecipient = null;
    }
    if (this.newMessageRecipientQuery.length >= 2) {
      this.recipientSearch$.next(this.newMessageRecipientQuery);
    } else {
      this.newMessageRecipientResults = [];
    }
  }

  selectRecipient(u: { id: number; displayName: string }) {
    this.newMessageRecipient = u;
    this.newMessageRecipientQuery = u.displayName;
    this.newMessageRecipientResults = [];
  }

  clearRecipient() {
    this.newMessageRecipient = null;
    this.newMessageRecipientQuery = '';
  }

  onAttachmentChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.newMessageAttachment = input.files?.[0] ?? null;
    if (this.newMessageAttachment) {
      const maxBytes = this.maxAttachmentSizeMb * 1024 * 1024;
      if (this.newMessageAttachment.size > maxBytes) {
        this.error = `Attachment exceeds maximum size of ${this.maxAttachmentSizeMb} MB`;
        this.newMessageAttachment = null;
        input.value = '';
      }
    }
  }

  /** True when there is no user-added content that would be lost on close. In reply mode, pre-filled topic is not counted. */
  get isNewMessageEmpty(): boolean {
    if (this.composeMode === 'reply') {
      return this.isHtmlEmpty(this.newMessageHtml) && !this.newMessageAttachment;
    }
    return !this.newMessageTopic.trim() && this.isHtmlEmpty(this.newMessageHtml) && !this.newMessageAttachment;
  }

  /** HTML editor may leave empty tags like <p></p> when content is deleted */
  private isHtmlEmpty(html: string): boolean {
    if (!html?.trim()) return true;
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return !text;
  }

  get hasNewMessageContent(): boolean {
    return !this.isHtmlEmpty(this.newMessageHtml);
  }

}
